import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Now, PlainDate } from 'temporal-luxon';
import { DatePicker, H5, Icon, Link, ModalWindow, Select, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { copyBlankConsentPdf } from './extract_blank_pdf';
import { usePtsBlankPdfUrls, usePtsDocumentModal, usePtsFinalDocumentUrl, } from './use_pts_document_modal.hook';
import { isDocumentVoidable } from '../../utils/signed_document_status.helpers';
import { FileDropZone } from '../file_drop_zone';
const PDF_MAX_FILE_SIZE_MB = 50;
const PDF_ACCEPTED_TYPES = '.pdf';
export const PtsDocumentModal = ({ isOpen, onClose, signedDocId, status, signatureDate, finalDocumentUrl, accountId, isNoPiiSite = false, apiClient, getApiErrorMessage, onComplete, canVoid = false, onVoid, canDownloadConsentPdf = true, completionDateUpdateMode = false, replacementUploadMode = false, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const today = Now.plainDateISO();
    // Correcting a date starts from the stored one so the picker opens on the value being
    // changed; completing a document starts from today.
    const initialDate = completionDateUpdateMode && signatureDate ? PlainDate.from(signatureDate) : today;
    const [selectedDate, setSelectedDate] = useState(initialDate);
    const [selectedFile, setSelectedFile] = useState(undefined);
    const [reasonForChange, setReasonForChange] = useState(undefined);
    const [isDownloadingBlank, setIsDownloadingBlank] = useState(false);
    // A terminally-voided PTS document (voided, or cancelled — the sent-and-never-touched variant)
    // must be read-only, same as the runtime-bundle DocumentModal — otherwise reopening it lets a
    // user upload a scan / complete a wet-ink consent that was already voided.
    const isTerminallyVoided = !!status && !isDocumentVoidable(status);
    // Update mode exists precisely to edit a completed document, so it opts out of view mode —
    // but never out of the voided guard.
    const isViewMode = (status === 'complete' && !completionDateUpdateMode && !replacementUploadMode) || isTerminallyVoided;
    const dateChangeReasonOptions = [
        { value: 'DBF', label: translate('label.pts_document.date_change_reason.dbf') },
        { value: 'DAF', label: translate('label.pts_document.date_change_reason.daf') },
    ];
    // The backend rejects an unchanged date, so the action stays disabled until the date
    // actually moves and a reason is chosen. Compared as ISO strings rather than with
    // PlainDate.equals, which temporal-luxon does not implement.
    const isDateChanged = !!selectedDate && !!signatureDate && selectedDate.toString() !== signatureDate;
    const canSubmitDateChange = isDateChanged && !!reasonForChange;
    const { blankPdfUrl, bundleUrl } = usePtsBlankPdfUrls({
        apiClient,
        signedDocId,
        accountId: accountId ?? '',
        // Collect, completed view, and voided view all need the unsigned copy.
        // Date-change and replacement modes do not print a blank form (SSP-4679).
        // Voided is included on purpose: SSP-3526 only blocks complete / date-change
        // / upload on a voided document, not the printable copy.
        enabled: isOpen && !completionDateUpdateMode && !replacementUploadMode,
    });
    const hasBlankPdf = !!blankPdfUrl || !!bundleUrl;
    const handleDownloadBlankPdf = useCallback(async () => {
        setIsDownloadingBlank(true);
        try {
            await copyBlankConsentPdf({
                blankPdfUrl,
                bundleUrl,
                mode: 'download',
                fileName: 'consent_document.pdf',
            });
        }
        catch {
            addNotification({ title: '', subtitle: translate('error.pts_document.download_blank'), type: 'error' });
        }
        finally {
            setIsDownloadingBlank(false);
        }
    }, [addNotification, blankPdfUrl, bundleUrl, translate]);
    const { submitPtsDocument, isSubmitting } = usePtsDocumentModal({
        apiClient,
        signedDocId,
        accountId,
        onComplete,
    });
    // The list read may not resolve the File `url`; fetch fresh by id in view mode
    // and fall back to the value threaded from the table row.
    const { finalDocumentUrl: fetchedFinalDocumentUrl } = usePtsFinalDocumentUrl({
        apiClient,
        signedDocId,
        accountId,
        enabled: isViewMode || replacementUploadMode,
    });
    const resolvedFinalDocumentUrl = fetchedFinalDocumentUrl ?? finalDocumentUrl;
    const handleClose = () => {
        setSelectedDate(initialDate);
        setSelectedFile(undefined);
        setReasonForChange(undefined);
        onClose();
    };
    const handleSubmit = async () => {
        if (replacementUploadMode) {
            if (!selectedFile) {
                return;
            }
            try {
                await submitPtsDocument({ file: selectedFile });
                addNotification({
                    title: '',
                    subtitle: translate('info.pts_document.upload_success'),
                    type: 'success',
                });
                handleClose();
            }
            catch (error) {
                const message = getApiErrorMessage?.(error) ?? translate('error.pts_document.upload');
                addNotification({ title: '', subtitle: message, type: 'error' });
            }
            return;
        }
        if (!selectedDate || (completionDateUpdateMode && !canSubmitDateChange)) {
            return;
        }
        try {
            await submitPtsDocument({
                signatureDate: selectedDate,
                file: completionDateUpdateMode ? undefined : selectedFile,
                reasonForChange: completionDateUpdateMode
                    ? reasonForChange?.value
                    : undefined,
            });
            addNotification({
                title: '',
                subtitle: completionDateUpdateMode
                    ? translate('info.pts_document.date_change_success')
                    : selectedFile
                        ? translate('info.pts_document.upload_success')
                        : translate('info.pts_document.complete_success'),
                type: 'success',
            });
            handleClose();
        }
        catch (error) {
            const message = getApiErrorMessage?.(error) ?? translate('error.pts_document.complete');
            addNotification({ title: '', subtitle: message, type: 'error' });
        }
    };
    const savingButton = (_jsx("div", { className: 'flex items-center gap-3', children: _jsx(Spinner, { size: 'md' }) }));
    const primaryActionButton = isViewMode
        ? { label: translate('generic.done'), onClick: handleClose, dataTestId: 'pts-document-done' }
        : {
            label: replacementUploadMode
                ? translate('action.pts_document.upload_replacement')
                : completionDateUpdateMode
                    ? translate('action.pts_document.save_date_change')
                    : translate('action.pts_document.complete'),
            onClick: handleSubmit,
            disabled: replacementUploadMode
                ? !selectedFile || isSubmitting
                : completionDateUpdateMode
                    ? !canSubmitDateChange || isSubmitting
                    : !selectedDate || isSubmitting,
            content: isSubmitting ? savingButton : undefined,
            dataTestId: 'pts-document-submit',
        };
    // Shown in collect and in view mode, including voided/cancelled. Completing or
    // uploading a scan is already blocked; the printable source copy is not (SSP-3526).
    const showBlankPdfDownload = hasBlankPdf && canDownloadConsentPdf && !completionDateUpdateMode && !replacementUploadMode;
    const blankPdfDownload = showBlankPdfDownload ? (_jsxs(Text, { size: 'sm', className: 'text-text-secondary', children: [translate('label.pts_document.download_blank_instructions'), ' ', _jsx(Link, { href: '#', "data-testid": 'pts-document-download-blank', onClick: (event) => {
                    event.preventDefault();
                    if (!isDownloadingBlank) {
                        void handleDownloadBlankPdf();
                    }
                }, children: _jsxs("span", { className: 'inline-flex items-center gap-1', children: [isDownloadingBlank ? _jsx(Spinner, { size: 'sm' }) : _jsx(Icon, { name: 'download', size: 'sm' }), translate('action.pts_document.download_blank')] }) })] })) : null;
    const showVoidButton = canVoid && !!onVoid;
    const voidButton = showVoidButton
        ? {
            label: translate('action.participant_documents.void_consent'),
            onClick: onVoid,
            disabled: isSubmitting,
            variant: 'secondary-destructive',
        }
        : undefined;
    const secondaryActionButtons = isViewMode
        ? voidButton && [voidButton]
        : voidButton
            ? [{ label: translate('generic.cancel'), onClick: handleClose, disabled: isSubmitting }, voidButton]
            : [{ label: translate('generic.cancel'), onClick: handleClose, disabled: isSubmitting }];
    return (_jsx(ModalWindow, { title: replacementUploadMode
            ? translate('label.pts_document.replacement_title')
            : completionDateUpdateMode
                ? translate('label.pts_document.date_change_title')
                : translate('label.pts_document.title'), isOpen: isOpen, closeWindow: handleClose, footerPrimaryActionButton: primaryActionButton, footerSecondaryActionButtons: secondaryActionButtons, width: 'base', dataTestId: 'pts-document-modal', children: _jsx("div", { className: 'flex flex-col gap-6 py-4', children: isViewMode ? (_jsxs(_Fragment, { children: [isTerminallyVoided && (_jsx(Text, { className: 'text-red-600', dataTestId: 'pts-document-voided-notice', children: translate('info.participant_documents.document_voided') })), _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(H5, { as: 'h3', className: 'text-sm font-medium', children: translate('label.pts_document.signature_date') }), _jsx(Text, { size: 'sm', className: 'text-text-secondary', dataTestId: 'pts-document-signature-date', children: signatureDate || translate('info.pts_document.no_signature') })] }), resolvedFinalDocumentUrl && canDownloadConsentPdf && (_jsx(Link, { href: resolvedFinalDocumentUrl, target: '_blank', rel: 'noopener noreferrer', "data-testid": 'pts-document-download', children: translate('action.pts_document.download') })), blankPdfDownload] })) : (_jsxs(_Fragment, { children: [blankPdfDownload, !completionDateUpdateMode && !replacementUploadMode && (_jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('label.pts_document.date_instructions') })), !replacementUploadMode && (_jsxs(_Fragment, { children: [_jsx(DatePicker, { id: 'pts-document-date', label: completionDateUpdateMode
                                    ? translate('label.pts_document.completion_date')
                                    : translate('label.pts_document.signature_date'), value: selectedDate, onChange: (date) => setSelectedDate(date), endDate: today, required: true }), completionDateUpdateMode && (_jsx(Select, { dataTestId: 'pts-document-date-change-reason', label: translate('label.pts_document.date_change_reason_label'), options: dateChangeReasonOptions, value: reasonForChange, onChange: setReasonForChange, placeholder: translate('label.pts_document.date_change_reason_placeholder'), width: 'full', required: true }))] })), (replacementUploadMode || !completionDateUpdateMode) && !isNoPiiSite && (_jsxs("div", { className: 'flex flex-col gap-2', children: [replacementUploadMode && resolvedFinalDocumentUrl && canDownloadConsentPdf && (_jsx(Link, { href: resolvedFinalDocumentUrl, target: '_blank', rel: 'noopener noreferrer', "data-testid": 'pts-document-download', children: translate('action.pts_document.download') })), _jsx(H5, { as: 'h3', className: 'text-sm font-medium', children: translate('label.pts_document.upload_label') }), _jsx(FileDropZone, { acceptedFileTypes: PDF_ACCEPTED_TYPES, acceptedExtensions: ['PDF'], maxFiles: 1, maxFileSize: PDF_MAX_FILE_SIZE_MB, description: translate('label.pts_document.upload_notice'), disabled: isSubmitting, onUpload: async (files) => {
                                    setSelectedFile(files[0]);
                                } }), selectedFile && (_jsx(Text, { size: 'sm', className: 'text-text-secondary', dataTestId: 'pts-document-selected-file', children: selectedFile.name }))] }))] })) }) }));
};
